import { FormRow } from '@creditornot/cb-form';
import { Clock as ClockIcon, Moon as MoonIcon } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import React from 'react';
import { Field, useField } from 'react-final-form';
import { useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CloseDeliveries, OpeningHoursType } from 'apis/merchant-onboarding-service/types';
import { WeeklyScheduleInput, WeeklyScheduleInputEntry } from 'common/components/weekly-schedules';
import { Typography as T } from 'common/typography';

import CowboyIcon from './CowboyIcon';
import NormalOpeningHoursEntryInput from './NormalOpeningHoursEntryInput';
import OpeningHoursTypeItem from './OpeningHoursTypeItem';
import { NormalOpeningHoursEntries, NormalOpeningHoursEntry } from './types';

const DescriptionText = styled(T.Body2)`
  display: block;
  margin-block-end: 16px;
`;

const OpeningHoursTypeContainer = styled.div`
  display: flex;

  div:last-child {
    margin-inline-end: 0;
  }
`;

const NormalOpeningHoursContainer = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding-block-start: 16px;
  padding-block-end: 16px;
`;

const NormalOpeningHoursRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${cbSemanticCssVars.textSecondary};
  padding-block-start: 8px;
  padding-block-end: 8px;
`;

const NormalOpeningHoursDayLabel = styled(T.Body3)`
  width: 200px;
  padding-inline-end: 24px;
`;

const OpeningHoursTab = () => {
  const intl = useIntl();

  const { input: openingHoursTypeInput } = useField<OpeningHoursType>('openingHoursType');
  const { input: complicatedOpeningHoursInput } = useField<WeeklyScheduleInputEntry[]>('complicatedOpeningHours');
  const { input: closeDeliveriesInput } = useField<CloseDeliveries>('closeDeliveries');

  const weekdayIntlIds: { [key: string]: string } = {
    monday: 'week.mon',
    tuesday: 'week.tue',
    wednesday: 'week.wed',
    thursday: 'week.thu',
    friday: 'week.fri',
    saturday: 'week.sat',
    sunday: 'week.sun',
  };

  const openingHoursTypeInputValue = openingHoursTypeInput.value;

  const isTwentyFourSevenHours = openingHoursTypeInputValue === 'twenty-four-seven';
  const isComplicatedHours = openingHoursTypeInputValue === 'complicated';

  const closeDeliveriesOptions: { label: string; value: CloseDeliveries }[] = [
    {
      label: intl.formatMessage({
        id: 'venue-data-collection.venue-information-form.close-deliveries-options.at-closing',
      }),
      value: 'at-closing',
    },
    {
      label: intl.formatMessage({
        id: 'venue-data-collection.venue-information-form.close-deliveries-options.30-minutes-before-closing',
      }),
      value: '30-minutes-before-closing',
    },
    {
      label: intl.formatMessage({
        id: 'venue-data-collection.venue-information-form.close-deliveries-options.60-minutes-before-closing',
      }),
      value: '60-minutes-before-closing',
    },
  ];

  const handleTypeChange = (newSelectedType: OpeningHoursType) => {
    openingHoursTypeInput.onChange(newSelectedType);

    if (newSelectedType === 'twenty-four-seven') {
      closeDeliveriesInput.onChange('at-closing');
    }
  };

  return (
    <>
      <DescriptionText>
        <FormattedMessage id="venue-data-collection.venue-information-form.opening-hours-tab-description" />
      </DescriptionText>

      <FormRow label={intl.formatMessage({ id: 'venue-data-collection.venue-information-form.opening-hours' })}>
        <OpeningHoursTypeContainer>
          <OpeningHoursTypeItem
            dataTestId={'venue-data-collection-opening-hours-normal-tab'}
            label={intl.formatMessage({ id: 'venue-data-collection.venue-information-form.normal' })}
            icon={<ClockIcon />}
            onClick={() => handleTypeChange('normal')}
            isActive={openingHoursTypeInputValue === 'normal'}
          />
          <OpeningHoursTypeItem
            dataTestId={'venue-data-collection-opening-hours-twenty-four-seven-tab'}
            label={intl.formatMessage({ id: 'venue-data-collection.venue-information-form.twentyFourSeven' })}
            icon={<MoonIcon />}
            onClick={() => handleTypeChange('twenty-four-seven')}
            isActive={openingHoursTypeInputValue === 'twenty-four-seven'}
          />
          <OpeningHoursTypeItem
            dataTestId={'venue-data-collection-opening-hours-it-is-complicated-tab'}
            label={intl.formatMessage({ id: 'venue-data-collection.venue-information-form.it-is-complicated' })}
            icon={<CowboyIcon />}
            onClick={() => handleTypeChange('complicated')}
            isActive={openingHoursTypeInputValue === 'complicated'}
          />
        </OpeningHoursTypeContainer>
      </FormRow>

      {!isTwentyFourSevenHours && (
        <>
          {isComplicatedHours ? (
            <FormRow label={intl.formatMessage({ id: 'venue-data-collection.venue-information-form.hours' })}>
              <Field<WeeklyScheduleInputEntry[]> name="complicatedOpeningHours">
                {({ input, meta }) =>
                  isComplicatedHours && (
                    <WeeklyScheduleInput
                      onChange={value => complicatedOpeningHoursInput.onChange(value)}
                      pristine={!!meta.pristine}
                      value={input.value}
                    />
                  )
                }
              </Field>
            </FormRow>
          ) : (
            <NormalOpeningHoursContainer>
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(dayOfWeek => (
                <div key={dayOfWeek}>
                  <Field<NormalOpeningHoursEntry> key={dayOfWeek} name={`normalOpeningHours.${dayOfWeek}`}>
                    {({ input }) => (
                      <NormalOpeningHoursRow data-testid={`row-${dayOfWeek}`}>
                        <NormalOpeningHoursDayLabel>
                          <FormattedMessage id={weekdayIntlIds[dayOfWeek]} />
                        </NormalOpeningHoursDayLabel>
                        <NormalOpeningHoursEntryInput value={input.value} onChange={input.onChange} />
                      </NormalOpeningHoursRow>
                    )}
                  </Field>

                  <Field<NormalOpeningHoursEntries> name="normalOpeningHours">
                    {({ meta }) =>
                      meta.error?.id && meta.error.values.dayOfWeek === dayOfWeek ? (
                        <InputAlert variant="error">
                          <span data-testid="opening-hours-validation-error">
                            <FormattedMessage id={meta.error.id} />
                          </span>
                        </InputAlert>
                      ) : null
                    }
                  </Field>
                </div>
              ))}
            </NormalOpeningHoursContainer>
          )}

          <FormRow
            label={intl.formatMessage({ id: 'venue-data-collection.venue-information-form.close-deliveries' })}
            labelHtmlFor="draft-venue-form-website"
          >
            <Field<CloseDeliveries> name="closeDeliveries">
              {field => <Select inputId="closeDeliveries" options={closeDeliveriesOptions} {...field.input} />}
            </Field>
          </FormRow>
        </>
      )}
    </>
  );
};

export default OpeningHoursTab;
